import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import CheckIcon from '@material-ui/icons/Check';
import { Field, Form, Formik } from 'formik';
import useApi from '../../../utils/useApi';
import UIDialogTitle from '../../UI/Dialog/Title/UIDialogTitle';
import { emptyObject, formattedCurrency } from '../../../utils/utils';
import { searchCatalogFuzzy } from '../../../utils/posFuzzyEngine';
import debounce from 'lodash/debounce';
import UIItemQuantityGuide from '../../UI/ItemQuantityGuide/UIItemQuantityGuide';
import { useCookies } from 'react-cookie';
import UILogo from '../../UI/Logo/UILogo';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&.Mui-selected': {
    backgroundColor: '#c4ced88a',
  },
  '&.Mui-selected:hover': {
    backgroundColor: '#c2d7eb',
  },
}));

const INITIAL_VISIBLE_COUNT = 15;
const SCROLL_STEP = 10;

const SaleSearchItem = ({ catalog = null, categories = null, loadingCatalog = false, ...children }) => {
  const [item, setItem] = useState(null);
  const [dataTable, setDataTable] = useState([]);
  const [visibleLimit, setVisibleLimit] = useState(INITIAL_VISIBLE_COUNT);
  // ação para click esquerdo
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [amountSelected, setAmountSelected] = useState(1);
  const [cookies, setCookie] = useCookies(['saleItemSearchCategory']);
  const categoriesFetchedRef = useRef(false);

  const initialState = {
    name: '',
    category: cookies.saleItemSearchCategory || null,
  };

  const handleClickLeft = (event, data) => {
    setItem(data);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseClickLeft = () => {
    setItem(null);
    setAnchorEl(null);
  };

  const [searchItemLoad, searchItemInfo] = useApi({
    debounceDelay: 300,
    method: 'get',
  });

  const [productsCategoryLoad, productsCategoryInfo] = useApi({
    method: 'get',
    url: '/products/categories',
    params: { isActive: 1 },
  });

  const [servicesCategoryLoad, servicesCategoryInfo] = useApi({
    method: 'get',
    url: '/services/categories',
    params: { isActive: 1 },
  });

  useEffect(() => {
    if ((!categories || categories.length === 0) && !categoriesFetchedRef.current) {
      categoriesFetchedRef.current = true;
      productsCategoryLoad();
      servicesCategoryLoad();
    }
  }, []);

  // Ao abrir o modal ou carregar o catálogo, exibe os itens imediatamente
  useEffect(() => {
    if (children.open) {
      setVisibleLimit(INITIAL_VISIBLE_COUNT);
      if (catalog && catalog.length > 0) {
        const catId = initialState.category ? (typeof initialState.category === 'object' ? initialState.category.id : initialState.category) : 'all';
        const initialResults = searchCatalogFuzzy('', catalog, 'all', catId);
        setDataTable(initialResults);
      } else if (initialState.category) {
        onSearch(initialState);
      }
    }
  }, [children.open, catalog]);

  function selectItem(data) {
    if (children.mobile) {
      handleCloseClickLeft();
    }

    const itemData = { ...data };
    if (Number(amountSelected) > parseFloat(itemData.amount))
      itemData.amountSelected = parseFloat(itemData.amount);
    else itemData.amountSelected = Number(amountSelected);

    children.selectItem(itemData);
    setAmountSelected(1);
  }

  async function onSearch(values) {
    setVisibleLimit(INITIAL_VISIBLE_COUNT);
    const catId = values.category ? (typeof values.category === 'object' ? values.category.id : Number(values.category)) : 'all';

    // 1. Busca fonética e instantânea caso o catálogo esteja em memória
    if (catalog && catalog.length > 0) {
      const results = searchCatalogFuzzy(values.name || '', catalog, 'all', catId);
      setDataTable(results);
      setCookie('saleItemSearchCategory', values.category, { path: '/' });
      return;
    }

    // 2. Fallback: requisição remota se catálogo ainda não carregado
    const searchValues = {
      ...values,
      category: values.category ? Number(values.category.id) : '',
    };

    if (!searchValues.name && !searchValues.category) return [];

    const productsParams = { ...emptyObject(searchValues), lacking: 0 };
    delete productsParams.amountSelected;

    let searchData = [];
    await searchItemLoad({
      params: productsParams,
      url: '/products',
      onCompleted: (res) => {
        if (res) {
          let products = res.data.map((p) => ({
            ...p,
            typeItem: 'product',
          }));
          searchData = searchData.concat(products);
        }
      },
    });

    const serviceParams = { ...emptyObject(searchValues), isActive: 1 };
    delete serviceParams.amountSelected;
    await searchItemLoad({
      params: serviceParams,
      url: '/services',
      onCompleted: (res) => {
        if (res) {
          let services = res.data.map((p) => ({
            ...p,
            typeItem: 'service',
          }));
          searchData = searchData.concat(services);
        }
      },
    });

    setCookie('saleItemSearchCategory', values.category, {
      path: '/',
    });
    setDataTable(searchData);
  }
  const debouncedOnSearch = debounce(onSearch, 200);

  function getCategory() {
    if (categories && categories.length > 0) {
      return categories;
    }

    if (servicesCategoryInfo.data && productsCategoryInfo.data) {
      return servicesCategoryInfo.data.concat(productsCategoryInfo.data);
    }

    if (servicesCategoryInfo.data !== null) {
      return servicesCategoryInfo.data;
    }

    if (productsCategoryInfo.data !== null) {
      return productsCategoryInfo.data;
    }

    return [];
  }

  const handleTableScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 120) {
      setVisibleLimit((prev) => Math.min(dataTable.length, prev + SCROLL_STEP));
    }
  };

  return (
    <Dialog
      open={children.open}
      onClose={children.handleModal}
      maxWidth="md"
      fullWidth
    >
      <Formik onSubmit={debouncedOnSearch} initialValues={initialState}>
        {({ values, setFieldValue, submitForm }) => (
          <Form>
            <UIDialogTitle onClose={children.handleModal}>
              Procurar Item
            </UIDialogTitle>
            <DialogContent>
              <Grid container spacing={children.mobile ? 0.7 : null}>
                <Grid item xs={12} md>
                  <Field
                    name="name"
                    label="Nome do item"
                    placeholder="Pesquise pelo nome do item"
                    fullWidth
                    as={TextField}
                    onChange={(ev) => {
                      const val = ev.target.value;
                      setFieldValue('category', null);
                      setFieldValue('name', val);
                      if (catalog && catalog.length > 0) {
                        onSearch({ name: val, category: null });
                      } else {
                        debouncedOnSearch({ name: val, category: null });
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md>
                  <Autocomplete
                    clearOnEscape
                    noOptionsText="Sem opções"
                    value={values.category}
                    disabled={
                      (productsCategoryInfo.loading &&
                        servicesCategoryInfo.loading) ||
                      values.name
                    }
                    onChange={(ev, category) => {
                      setFieldValue('category', category);
                      submitForm(ev);
                    }}
                    options={getCategory()}
                    getOptionLabel={(category) => category.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Categoria"
                        name="category"
                        variant="standard"
                        placeholder="Informe a categoria do produto"
                      />
                    )}
                  />
                </Grid>
                <Grid
                  container
                  justify="flex-end"
                  alignItems="center"
                  item
                  xs={12}
                >
                  <Button
                    onClick={() => submitForm(values)}
                    color="primary"
                    startIcon={
                      (searchItemInfo.loading && (
                        <CircularProgress size="0.9rem" color="inherit" />
                      )) ||
                      (searchItemInfo.loading && (
                        <CircularProgress size="0.9rem" color="inherit" />
                      ))
                    }
                    autoFocus
                  >
                    Pesquisar
                  </Button>
                </Grid>
                <Grid container justify="center" alignItems="center" item xs>
                  {searchItemInfo.error &&
                    dataTable.length === 0 &&
                    'Sem resultado'}
                </Grid>
                {dataTable.length > 0 && (
                  <Grid item xs={12}>
                    <TableContainer
                      onScroll={handleTableScroll}
                      style={{ maxHeight: '55vh', overflowY: 'auto' }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>Preço</TableCell>
                            <TableCell>Categoria</TableCell>
                            {!children.mobile && (
                              <>
                                <TableCell>Item</TableCell>
                                <TableCell style={{ minWidth: 100 }}>
                                  <TextField
                                    value={amountSelected || 1}
                                    onChange={(ev) => {
                                      if (ev.target.value >= 0) {
                                        setAmountSelected(ev.target.value);
                                      }
                                    }}
                                    label="Quantos"
                                    type="number"
                                    fullWidth
                                  />
                                </TableCell>
                                <TableCell align="center">Selecionar</TableCell>
                              </>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {searchItemInfo.loading && (
                            <TableRow>
                              <TableCell colSpan={6} align="center">
                                <CircularProgress size={24} />
                              </TableCell>
                            </TableRow>
                          )}
                          {dataTable.slice(0, visibleLimit).map((data) => (
                            <StyledTableRow
                              hover
                              style={{ cursor: 'pointer' }}
                              selected={
                                children.listItems &&
                                children.listItems[
                                data.typeItem === 'product'
                                  ? 'products'
                                  : 'services'
                                ] &&
                                children.listItems[
                                data.typeItem === 'product'
                                  ? 'products'
                                  : 'services'
                                ][data.id]
                              }
                              key={`${data.typeItem}-${data.id}`}
                              onClick={
                                children.mobile
                                  ? (ev) => handleClickLeft(ev, data)
                                  : () => selectItem(data)
                              }
                            >
                              <TableCell component="th" scope="row">
                                <UILogo
                                  name={
                                    children.listItems &&
                                    children.listItems[
                                    data.typeItem === 'product'
                                      ? 'products'
                                      : 'services'
                                    ] &&
                                    children.listItems[
                                    data.typeItem === 'product'
                                      ? 'products'
                                      : 'services'
                                    ][data.id]
                                  }
                                  lastName={` ${data.name}`}
                                  size={16}
                                />
                              </TableCell>
                              <TableCell>
                                {formattedCurrency(data.value || data.valueUn || 0)}
                              </TableCell>
                              <TableCell>
                                {data.categoryName ||
                                  getCategory().filter(
                                    (category) => String(category.id) === String(data.category?.id || data.category || data.categoryId),
                                  )[0]?.name || '-'}
                              </TableCell>

                              {!children.mobile && (
                                <>
                                  <TableCell style={{ fontWeight: 'bold' }}>
                                    {data.typeItem === 'product'
                                      ? 'Produto'
                                      : 'Serviço'}
                                  </TableCell>

                                  <TableCell>
                                    {data.typeItem === 'product'
                                      ? `Estoque: ${data.amount !== undefined ? data.amount : '-'}`
                                      : 'Serviço'}
                                  </TableCell>
                                  <TableCell align="center">
                                    <span title="Adicionar produto">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          selectItem(data);
                                        }}
                                      >
                                        <CheckIcon />
                                      </IconButton>
                                    </span>
                                  </TableCell>
                                </>
                              )}
                            </StyledTableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {dataTable.length > visibleLimit && (
                      <div
                        style={{
                          textAlign: 'center',
                          padding: '8px',
                          fontSize: '0.78rem',
                          color: '#666',
                        }}
                      >
                        Exibindo {visibleLimit} de {dataTable.length} itens. Role para ver mais...
                      </div>
                    )}
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions />
          </Form>
        )}
      </Formik>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseClickLeft}
      >
        <MenuItem onClick={() => selectItem(item)}>Selecionar produto</MenuItem>
      </Menu>
    </Dialog>
  );
};

export default SaleSearchItem;
